var __awaiter = this && this.__awaiter || function (thisArg, _arguments, P, generator) {
  function adopt(value) {
    return value instanceof P ? value : new P(function (resolve) {
      resolve(value);
    });
  }
  return new (P || (P = Promise))(function (resolve, reject) {
    function fulfilled(value) {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    }
    function rejected(value) {
      try {
        step(generator["throw"](value));
      } catch (e) {
        reject(e);
      }
    }
    function step(result) {
      result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected);
    }
    step((generator = generator.apply(thisArg, _arguments || [])).next());
  });
};
/* eslint-disable @typescript-eslint/no-floating-promises */
import { useCallback, useEffect, useRef, useState } from 'react';
import { v4 as uuidV4 } from 'uuid';
import pennyBroadcast, { BroadcastType, usePennySubscriber } from '../utils/pennyBroadcast';
import { useRealTimeAudioPlayer } from './useRealTimeAudioPlayer';
import { useRealTimeAudioRecorder } from './useRealTimeAudioRecorder';
import { useRealTimeWebSocket } from './useRealTimeWebSocket';
import { placementContextHandler } from '../components/Realtime/PlacementContextHandler/ContextHandler';
/**
 * This hook orchestrates the work between useRealTimeAudioPlayer, useRealTimeAudioRecorder, and useRealTimeWebSocket to facilitate
 * user interactions with real-time voice mode.
 *
 * @param locale - locale code of the user
 * @param realtimeContext - contextual information about the user
 * @param analytics - pcln logger instance
 * @param baseUrl - the base url of the endpoint to connect to
 * @param onLoadingChange - callback function for when the loading status changes
 * @param onOpenStateChange - callback function for when the voice mode opens
 * @returns an object containing all of the state that needs to be returned to the component that is using this hook and functions to
 * support interaction with real-time chat
 *  - `isActive` - Whether or not the chat coordinator has finished activating and initializing
    - `isRecording` - Whether or not audio is being collected
    - `isConnected` - Whether or not the WebSocket connection has connected
    - `isMuted` - Whether or not audio recording is paused
    - `messages` - The list of transcribed messages from both the user and the assistant
    - `marker` - The marker that is currently being rendered (if present)
    - `mediaStream` - The media stream object
    - `startChat` - The function to start the chat
    - `stopChat` - The function to stop the chat
    - `toggleMute` - The function to toggle mute on/off
 */
export function useRealTimeChatCoordinator({
  locale,
  realtimeContext,
  analytics,
  baseUrl,
  onLoadingChange,
  onOpenStateChange,
  pennysid,
  requestFrom
}) {
  // State
  const [messages, setMessages] = useState([]);
  const [marker, setMarker] = useState(null);
  const [isActive, setIsActive] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  // Refs
  const chatMessagesRef = useRef([]);
  const wsRef = useRef(null);
  const playAudioRef = useRef();
  const instanceRef = useRef(uuidV4());
  // Initialize audio player
  const {
    playAudio,
    clearQueue
  } = useRealTimeAudioPlayer(analytics);
  // Keep playAudio reference current
  useEffect(() => {
    playAudioRef.current = playAudio;
  }, [playAudio]);
  // WebSocket message handler
  const handleMessage = useCallback(message => {
    var _a;
    switch (message.type) {
      case 'session.created':
        placementContextHandler(wsRef, realtimeContext);
        break;
      case 'realtime_marker':
        setMarker(message.marker);
        break;
      case 'conversation.item.deleted':
        setMessages(prev => prev.filter(item => item.item_id !== message.item_id));
        break;
      case 'realtime_halt_playback':
        localStorage.setItem('isAudioInterrupted', 'yes');
        clearQueue();
        break;
      case 'response.audio.delta':
        localStorage.setItem('isAudioInterrupted', 'no');
        (_a = playAudioRef.current) === null || _a === void 0 ? void 0 : _a.call(playAudioRef, message.delta);
        break;
      case 'input_audio_buffer.speech_started':
        localStorage.setItem('isAudioInterrupted', 'yes');
        break;
      case 'conversation.item.input_audio_transcription.completed':
        if (message.transcript) {
          setMessages(prev => [...prev, {
            role: 'user',
            content: message.transcript
          }]);
        }
        break;
      case 'response.audio_transcript.done':
        onLoadingChange === null || onLoadingChange === void 0 ? void 0 : onLoadingChange(false);
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: message.transcript,
          item_id: message.item_id
        }]);
        break;
    }
  }, [onLoadingChange]);
  // Initialize WebSocket
  const {
    isConnected,
    sendMessage
  } = useRealTimeWebSocket({
    url: baseUrl,
    locale,
    realtimeContext,
    onMessage: handleMessage,
    analytics,
    pennysid,
    requestFrom
  });
  // Handle incoming audio chunks
  const handleAudioChunk = useCallback(base64AudioData => {
    var _a;
    (_a = wsRef.current) === null || _a === void 0 ? void 0 : _a.sendMessage({
      type: 'input_audio_buffer.append',
      audio: base64AudioData
    });
  }, []);
  // Initialize audio recorder
  const {
    isRecording,
    isMuted,
    mediaStream,
    startRecording,
    stopRecording,
    toggleMute
  } = useRealTimeAudioRecorder(handleAudioChunk, analytics);
  // Keep wsRef current
  useEffect(() => {
    wsRef.current = {
      sendMessage
    };
  }, [sendMessage]);
  // Keep message ref in sync
  useEffect(() => {
    chatMessagesRef.current = messages;
  }, [messages]);
  const commit = useCallback(() => {
    var _a;
    (_a = wsRef.current) === null || _a === void 0 ? void 0 : _a.sendMessage({
      type: 'input_audio_buffer.commit'
    });
  }, []);
  const startChat = useCallback(() => __awaiter(this, void 0, void 0, function* () {
    if (isActive || !isConnected) return;
    try {
      yield startRecording();
      setIsActive(true);
      onLoadingChange === null || onLoadingChange === void 0 ? void 0 : onLoadingChange(true);
    } catch (error) {
      analytics.logError({
        loggedFrom: 'useRealTimeChatCoordinator.startChat',
        message: 'Failed to start chat',
        error
      });
    }
  }), [isActive, isConnected, startRecording, onLoadingChange, analytics]);
  const stopChat = useCallback(() => {
    if (!isActive) return;
    stopRecording();
    localStorage.setItem('isAudioInterrupted', 'yes');
    clearQueue();
    setIsActive(false);
    onLoadingChange === null || onLoadingChange === void 0 ? void 0 : onLoadingChange(false);
  }, [isActive, stopRecording, clearQueue, commit, onLoadingChange]);
  // Auto-start chat when connected
  useEffect(() => {
    if (isConnected && isInitializing) {
      startChat();
      setIsInitializing(false);
    }
  }, [isConnected, isInitializing, startChat]);
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (isActive) {
        stopChat();
      }
    };
  }, [stopChat]);
  // initialize Penny Broadcast
  const pennyBroadcastListener = useCallback(event => {
    if (event.data.type === BroadcastType.NEW_REALTIME_VOICE_INSTANCE && event.data.instanceId !== instanceRef.current) {
      setIsActive(false);
      clearQueue();
      stopChat();
      onOpenStateChange(false);
    }
  }, []);
  usePennySubscriber(pennyBroadcastListener);
  useEffect(() => {
    pennyBroadcast({
      type: BroadcastType.NEW_REALTIME_VOICE_INSTANCE,
      instanceId: instanceRef.current,
      sender: 'useRealTimeChatCoordinator.useEffect'
    });
  }, []);
  return {
    isActive,
    isRecording,
    isConnected,
    isMuted,
    messages,
    marker,
    mediaStream,
    startChat,
    stopChat,
    toggleMute,
    setMarker
  };
}